// iter216 · Edit Sheet
//
// Modal overlay used by the owner Edit button on both VendorPublic and
// PlaceProfile. Fields shown vary by `kind`:
//   vendor:   banner, description, WhatsApp, Instagram
//   business: banner, description, WhatsApp, Instagram, hours, address
//
// Banner uploads go through the existing `/api/upload/image` endpoint
// which routes into Cloudflare Images and returns compressed variants
// (thumb 150 / medium 400 / full 750 px). Vendor avatar is NEVER shown
// here — locked to the ID face-crop per iter214.
//
// UI rules: Playfair headers, DM Sans body ≥16 px, inputs ≥16 px,
// 48 px touch targets, solid #171717 modal background.
import { useState } from "react";
import { X, Loader2, Upload, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const GOLD = "#E8B84B", CREAM = "#F5F1E8", MUTED = "#9A9490";
const INPUT = {
  background: "#0B0B0B", color: CREAM, border: `1px solid ${GOLD}`,
  borderRadius: 10, padding: "14px", fontSize: 16, minHeight: 48, width: "100%",
};
const LABEL = {
  fontFamily: "'DM Sans', sans-serif", fontSize: 11, letterSpacing: 1,
  textTransform: "uppercase", color: GOLD, marginBottom: 6, display: "block",
};

export default function EditSheet({ open, onClose, kind, profile, onSaved }) {
  const [form, setForm] = useState({
    background_image: profile?.background_image || "",
    description: profile?.description || profile?.bio || "",
    whatsapp: profile?.whatsapp || "",
    instagram: profile?.instagram || "",
    hours: profile?.hours || "",
    address: profile?.address || "",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const uploadBanner = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data?.urls?.full || data?.urls?.medium || data?.url || "";
      if (!url) throw new Error("upload_no_url");
      setForm((f) => ({ ...f, background_image: url }));
      toast.success("Banner uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const endpoint = kind === "vendor" ? "/vendor/me" : "/business/me";
      const payload = {
        background_image: form.background_image || null,
        whatsapp: form.whatsapp || null,
        instagram: form.instagram || null,
      };
      if (kind === "vendor") payload.bio = form.description || null;
      else payload.description = form.description || null;
      if (kind === "business") {
        payload.hours = form.hours || null;
        payload.address = form.address || null;
      }
      const { data } = await api.patch(endpoint, payload);
      toast.success("Saved");
      onSaved?.(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div
      role="dialog" aria-modal="true"
      data-testid="edit-sheet"
      style={{
        position: "fixed", inset: 0, zIndex: 80, background: "rgba(0,0,0,0.75)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#171717", width: "min(560px, 100%)",
          borderTopLeftRadius: 20, borderTopRightRadius: 20,
          maxHeight: "92vh", overflowY: "auto",
          padding: "20px 20px calc(env(safe-area-inset-bottom, 0px) + 20px) 20px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: CREAM }}>
            Edit profile
          </h2>
          <button
            data-testid="edit-sheet-close"
            onClick={onClose}
            style={{ minHeight: 48, minWidth: 48, color: GOLD, background: "transparent", border: "none" }}
            aria-label="Close"
          >
            <X className="w-5 h-5" strokeWidth={1.5}/>
          </button>
        </div>

        {/* Locked avatar notice — vendor only */}
        {kind === "vendor" && (
          <div
            data-testid="edit-sheet-avatar-locked"
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 12px", marginBottom: 16, borderRadius: 10,
              background: "rgba(232,184,75,0.08)", border: "1px solid rgba(232,184,75,0.30)",
              color: CREAM, fontFamily: "'DM Sans', sans-serif", fontSize: 14,
            }}
          >
            <Lock className="w-4 h-4" strokeWidth={2} style={{ color: GOLD }}/>
            Identity photo — from ID verification — cannot be changed
          </div>
        )}

        {/* Banner */}
        <div style={{ marginBottom: 16 }}>
          <label style={LABEL}>Banner photo</label>
          {form.background_image ? (
            <div style={{
              height: 140, borderRadius: 12, backgroundSize: "cover",
              backgroundPosition: "center", marginBottom: 8,
              backgroundImage: `url(${form.background_image})`,
            }}/>
          ) : null}
          <label
            data-testid="edit-sheet-banner-upload"
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              minHeight: 48, padding: "10px 18px", borderRadius: 999,
              background: "transparent", color: GOLD,
              border: `1px solid ${GOLD}`, fontSize: 15, fontWeight: 600,
              cursor: uploading ? "wait" : "pointer",
            }}
          >
            {uploading
              ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5}/>
              : <Upload className="w-4 h-4" strokeWidth={1.5}/>}
            {form.background_image ? "Replace banner" : "Upload banner"}
            <input type="file" accept="image/*" style={{ display: "none" }}
                   onChange={(e) => uploadBanner(e.target.files?.[0])}
                   disabled={uploading}/>
          </label>
        </div>

        {/* Description */}
        <div style={{ marginBottom: 16 }}>
          <label style={LABEL}>Description</label>
          <textarea
            data-testid="edit-sheet-description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={4} maxLength={800}
            style={{ ...INPUT, resize: "vertical", minHeight: 120, fontFamily: "'DM Sans', sans-serif" }}
          />
        </div>

        {/* WhatsApp */}
        <div style={{ marginBottom: 16 }}>
          <label style={LABEL}>WhatsApp</label>
          <input
            data-testid="edit-sheet-whatsapp"
            value={form.whatsapp}
            onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
            placeholder="+53555…"
            style={INPUT}
          />
        </div>

        {/* Instagram */}
        <div style={{ marginBottom: 16 }}>
          <label style={LABEL}>Instagram (optional)</label>
          <input
            data-testid="edit-sheet-instagram"
            value={form.instagram}
            onChange={(e) => setForm({ ...form, instagram: e.target.value })}
            placeholder="@handle"
            style={INPUT}
          />
        </div>

        {/* Business-only fields */}
        {kind === "business" && (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={LABEL}>Hours</label>
              <input
                data-testid="edit-sheet-hours"
                value={form.hours}
                onChange={(e) => setForm({ ...form, hours: e.target.value })}
                placeholder="Mon–Sun 6 pm – 2 am"
                style={INPUT}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={LABEL}>Address</label>
              <input
                data-testid="edit-sheet-address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Street, Neighborhood, Havana"
                style={INPUT}
              />
            </div>
          </>
        )}

        <button
          data-testid="edit-sheet-save"
          onClick={save}
          disabled={saving}
          style={{
            width: "100%", minHeight: 56, borderRadius: 12, marginTop: 6,
            background: GOLD, color: "#0B0B0B", border: "none",
            fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 600,
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
